import * as _ from 'lodash'
import { network, docC, documentC } from '../globals'
import { throwError, unreachable } from '../util'

import { nodeInfo, NodeInfo, InputNodeInfo } from './nodeInfo'
import { runCodeToResult, responseCountInput } from './run'
import { lastNodeIdInSteps, parentSteps } from './steps'

import * as Model from '../types/document'
import { Stack, stackBottom, StackFrame, CallStackFrame, ListOpStackFrame, RunOnNodeStackFrame } from '../types/stack'
import { Cursor, CursorV } from '../lib/Cursor'
import { Result, valueOrUndefined } from '../types/result'

import { Path } from '../lib/Path'

export const stackToBlockPath = network.valueFunc('stackToBlockPath', (stack: Stack): Path => {
  const lastFrame = stackBottom(stack)
  if (lastFrame.type === 'editor') {
    return lastFrame.definitionP
  } else if (lastFrame.type === 'call') {
    const callC = CallStackFrame.callC(lastFrame)
    const definitionId = callC.select('definition').get()
    return Cursor.selectPattern(documentC._('definitions'), {id: definitionId}).path()
  } else if (lastFrame.type === 'list-op') {
    const callC = ListOpStackFrame.callC(lastFrame)
    return callC._('block').path()
  } else if (lastFrame.type === 'run-on-node') {
    return lastFrame.definitionP
  } else if (lastFrame.type === 'scratch') {
    return documentC._('scratch').path()
  } else return unreachable(lastFrame)
})

export function stackToBlockC(stack: Stack) {
  return docC<Model.Block>(stackToBlockPath(stack))
}

export const evaluateNode = network.valueFunc('evaluateNode', (nodeId: Model.NodeId, stack: Stack): Result<any> => {
  if (nodeId === "id_node_254884_21") {
    console.log("GOT IT")
  }
  if (!nodeId) { throwError('evaluateNode needs nodeId') }
  if (!stack) { throwError('evaluateNode needs stack') }
  const blockC = stackToBlockC(stack)
  const info = nodeInfo(blockC, nodeId)
  if (!info) {
    return {type: 'error', error: new Error('nodeInfo failed on ' + JSON.stringify(stack) + ', ' + JSON.stringify(nodeId))}
  }
  if (info.isHigher) {
    // TODO: look higher in the stack, if you're in a list op
    if (stackBottom(stack).type === 'list-op') {
      return evaluateNode(nodeId, _.initial(stack))
    } else {
      return {type: 'error', error: new Error('nodeInfo failed on ' + JSON.stringify(stack) + ', ' + JSON.stringify(nodeId))}
    }
  }
  switch (info.type) {
  case 'input':
    return evaluateInputNode(nodeId, stack, info)
  case 'graph':
    const callId = info.definingCallId
    const graphC = NodeInfo.graphC(info)
    return evaluateCall(graphC._('calls')._(callId), stack)
  case 'switch-output':
    const switchC = NodeInfo.switchC(info)
    for (let caseC of CursorV.elems(switchC.select('cases').fetch())) {
      const conditionC = CursorV.unwrapMaybe(caseC.select('condition'))
      if (conditionC) {
        const conditionResult = evaluateSteps(conditionC.unfetch(), stack)
        if (conditionResult.type !== 'value') {
          // propagate error in condition. (semantics question: we could just go to the next case?)
          return conditionResult
        }
        if (!conditionResult.value) {
          continue // abort case, on to next case
        }
      }

      // this is our case! let's run it
      const stepsResult = evaluateSteps(caseC.select('steps').unfetch(), stack)
      return {...stepsResult, caseTaken: caseC.lastStep()}
    }
    return {type: 'error', error: new Error('no condition holds')}
  default:
    return unreachable(info)
  }
})

function evaluateInputNode(nodeId: Model.NodeId, stack: Stack, info: InputNodeInfo): Result<any> {
  const lastFrame = stackBottom(stack)
  if (lastFrame.type === 'editor') {
    // This is an editor frame, which gets its inputs from the example inputs in the doc
    const input = NodeInfo.nodeC(info).get() as Model.DefinitionInput
    return runCodeToResult("return " + input.settings[input.activeSetting])
  } else if (lastFrame.type === 'call') {
    // This is a call frame, which gets its inputs from the next-highest frame
    const call = CallStackFrame.callC(lastFrame).get()
    const input = _.find(call.inputs, {inside: nodeId})
    if (!input) { throw new Error('input not found with inside = ' + nodeId) }
    return evaluateNode(input.outside, _.initial(stack))
  } else if (lastFrame.type === 'list-op') {
    // This is a list-op frame, which gets its inputs from elements/indices of
    // the next-highest frame
    const call = ListOpStackFrame.callC(lastFrame).get()
    if (nodeId === call.elementNodeId) {
      const inputResult = evaluateNode(call.inputs[0].outside, _.initial(stack))
      if (inputResult.type === 'value') {
        if (inputResult.value instanceof Array || inputResult.value instanceof Object) {
          return {type: 'value', value: inputResult.value[lastFrame.index]}
        } else {
          return {type: 'error', error: new Error('Bad input')}
        }
      } else {
        return {type: 'error', error: new Error('Error in input')}
      }
    } else if (nodeId === call.indexNodeId) {
      return {type: 'value', value: lastFrame.index}
    } else {
      return {type: 'error', error: new Error('Unknown input node in list op body')}
    }
  } else if (lastFrame.type === 'run-on-node') {
    // This is a run-on-node frame, which gets its inputs from a
    // stack-provided node in the next-highest frame
    return evaluateNode(lastFrame.nodeId, _.initial(stack))
  } else if (lastFrame.type === 'scratch') {
    throw new Error("scratch block isn't supposed to have inputs! how did this happen...")
  } else return unreachable(lastFrame)
}

export const evaluateCall = network.valueFunc('evaluateCall', (callC: Cursor<Model.Call>, stack: Stack): Result<any> => {
  if (stack.length > 10) {
    return {type: 'error', error: new Error('Max stack depth reached')}
  }

  const call = callC.get()

  switch (call.callType) {
  case 'code': {
    // Resolve inputs
    const resolvedInputs = {}
    let inputError: Result<any> | undefined
    call.inputs.forEach(({outside, inside}) => {
      const result = evaluateNode(outside, stack)
      if (result.type === 'value') {
        resolvedInputs[inside] = result.value
      } else {
        inputError = result
        return
      }
    })
    if (inputError) { return inputError }
    let body = call.code
    if (!body.includes("return")) {  // TODO: this is terrible
      body = "return " + body
    }
    if (body.includes("webRequest")) {  // TODO: also terrible!
      responseCountInput.get()  // just declare a dependence dude
    }
    return runCodeToResult(body, resolvedInputs) }
  case 'definition': {
    const definitionC = documentC.select('definitions').fetch().call(CursorV.selectPattern, {id: call.definition})
    const newStack: Stack = [...stack, {type: 'call', callP: callC.path()}]
    return evaluateBlock(definitionC.unfetch(), newStack) }
  case 'list-op': {
    const inputResult = evaluateNode(call.inputs[0].outside, stack)
    if (inputResult.type === 'value') {
      if (inputResult.value instanceof Array) {
        const perElementFunc = (index: number) => {
          const newStack: Stack = [...stack, {type: 'list-op', callP: callC.path(), index: index}]
          return valueOrUndefined(evaluateBlock((callC as Cursor<Model.ListOpCall>)._('block'), newStack))
        }
        if (call.listOpType === 'map') {
          return {type: 'value', value: _.range(inputResult.value.length).map(perElementFunc)}
        } else if (call.listOpType === 'filter') {
          return {type: 'value', value: inputResult.value.filter((_elem, index) => perElementFunc(index))}
        } else {
          return {type: 'error', error: new Error('Unknown list operation')}
        }
      } if (inputResult.value instanceof Object) {
        const perElementFunc = (key: string) => {
          const newStack: Stack = [...stack, {type: 'list-op', callP: callC.path(), index: key}]
          return valueOrUndefined(evaluateBlock((callC as Cursor<Model.ListOpCall>)._('block'), newStack))
        }
        if (call.listOpType === 'map') {
          return {type: 'value', value: _.mapValues(inputResult.value, (_value: any, key: string) => perElementFunc(key))}
        } else if (call.listOpType === 'filter') {
          return {type: 'value', value: _.filter(inputResult.value, (_elem, index) => perElementFunc(index))}
        } else {
          return {type: 'error', error: new Error('Unknown list operation')}
        }
      } else {
        return {type: 'error', error: new Error('Input must be array')}  
      }
    } else {
      return {type: 'error', error: new Error('Problem with input')}
    }
  }
  }
})

export const evaluateBlock = network.valueFunc('evaluateBlock', (definitionC: Cursor<Model.Block>, stack: Stack) => {
  return evaluateSteps(definitionC.select('steps'), stack)
})

export const lastNodeIdInStepsC = network.valueFunc('lastNodeIdInStepsByPath', (stepsC: Cursor<Model.Steps>) => {
  return lastNodeIdInSteps(stepsC.get())
}, _.isEqual)

export const evaluateSteps = network.valueFunc('evaluateSteps', (stepsC: Cursor<Model.Steps>, stack: Stack): Result<any> => {
  const lastNodeId = lastNodeIdInStepsC(stepsC)
  if (!lastNodeId) {
    return {type: 'value', value: undefined}
    // return {type: 'error', error: new Error('Steps are empty at ' + JSON.stringify(stepsC.path()))}
  }
  return evaluateNode(lastNodeId, stack)
})





export const runOnNode = network.valueFunc('runOnNode', (nodeId: Model.NodeId, definitionC: Cursor<Model.Block>, stack: Stack) => {
  const newStack: Stack = [...stack, {type: 'run-on-node', nodeId, definitionP: definitionC.path()}]
  return evaluateSteps(definitionC.select('steps'), newStack)
})



// WEBPACK FOOTER //
// ./src/model/evaluate.ts